import type { AgeRating, AgeRatingAgency } from '../types';

/**
 * Rating agency preference by region.
 * Priority order: most commonly used rating agency in that region comes first.
 */
export const RATING_AGENCY_BY_REGION: Record<string, AgeRatingAgency[]> = {
  // North America
  US: ['ESRB', 'IARC'],
  CA: ['ESRB', 'IARC'],
  MX: ['DJCTQ', 'IARC'],

  // Europe
  EU: ['PEGI', 'IARC'],
  GB: ['PEGI', 'IARC'],
  DE: ['USK', 'PEGI', 'IARC'],
  FR: ['PEGI', 'IARC'],
  IT: ['PEGI', 'IARC'],
  ES: ['PEGI', 'IARC'],
  NL: ['PEGI', 'IARC'],
  BE: ['PEGI', 'IARC'],
  SE: ['PEGI', 'IARC'],
  NO: ['PEGI', 'IARC'],
  DK: ['PEGI', 'IARC'],
  FI: ['PEGI', 'IARC'],
  PL: ['PEGI', 'IARC'],
  CZ: ['PEGI', 'IARC'],
  RO: ['PEGI', 'IARC'],
  RU: ['PEGI', 'IARC'],
  UA: ['PEGI', 'IARC'],

  // Asia Pacific
  JP: ['CERO', 'IARC'],
  KR: ['GRAC', 'IARC'],
  CN: ['IARC'],
  TW: ['IARC'],
  AU: ['ACB', 'IARC'],
  NZ: ['OFLC', 'IARC'],
  SG: ['IARC'],
  HK: ['IARC'],
  TH: ['IARC'],

  // South Africa
  ZA: ['FPB', 'IARC'],

  // South America
  BR: ['CLASSIND', 'IARC'],
  AR: ['IARC'],
  CL: ['IARC'],

  DEFAULT: ['ESRB', 'PEGI', 'IARC'],
};

export const AGE_RATING_REGION_OPTIONS: Array<{ code: string; label: string; agency: string }> = [
  { code: 'AR', label: 'Argentina', agency: 'IARC' },
  { code: 'AU', label: 'Australia', agency: 'ACB' },
  { code: 'BE', label: 'Belgium', agency: 'PEGI' },
  { code: 'BR', label: 'Brazil', agency: 'ClassInd' },
  { code: 'CA', label: 'Canada', agency: 'ESRB' },
  { code: 'CL', label: 'Chile', agency: 'IARC' },
  { code: 'CN', label: 'China', agency: 'IARC' },
  { code: 'CZ', label: 'Czech Republic', agency: 'PEGI' },
  { code: 'DK', label: 'Denmark', agency: 'PEGI' },
  { code: 'EU', label: 'Europe', agency: 'PEGI' },
  { code: 'FI', label: 'Finland', agency: 'PEGI' },
  { code: 'FR', label: 'France', agency: 'PEGI' },
  { code: 'DE', label: 'Germany', agency: 'USK' },
  { code: 'HK', label: 'Hong Kong, China', agency: 'IARC' },
  { code: 'IT', label: 'Italy', agency: 'PEGI' },
  { code: 'JP', label: 'Japan', agency: 'CERO' },
  { code: 'MX', label: 'Mexico', agency: 'DJCTQ' },
  { code: 'NL', label: 'Netherlands', agency: 'PEGI' },
  { code: 'NZ', label: 'New Zealand', agency: 'OFLC' },
  { code: 'NO', label: 'Norway', agency: 'PEGI' },
  { code: 'PL', label: 'Poland', agency: 'PEGI' },
  { code: 'RO', label: 'Romania', agency: 'PEGI' },
  { code: 'RU', label: 'Russia', agency: 'PEGI' },
  { code: 'SG', label: 'Singapore', agency: 'IARC' },
  { code: 'ZA', label: 'South Africa', agency: 'FPB' },
  { code: 'KR', label: 'South Korea', agency: 'GRAC' },
  { code: 'ES', label: 'Spain', agency: 'PEGI' },
  { code: 'SE', label: 'Sweden', agency: 'PEGI' },
  { code: 'TW', label: 'Taiwan, China', agency: 'IARC' },
  { code: 'TH', label: 'Thailand', agency: 'IARC' },
  { code: 'UA', label: 'Ukraine', agency: 'PEGI' },
  { code: 'GB', label: 'United Kingdom', agency: 'PEGI' },
  { code: 'US', label: 'United States', agency: 'ESRB' },
];

export const AGE_RATING_REGION_CODES = AGE_RATING_REGION_OPTIONS.map((option) => option.code);

export function getAgeRatingRegionLabel(code?: string | null): string | null {
  if (!code) return null;
  const option = AGE_RATING_REGION_OPTIONS.find((entry) => entry.code === code);
  if (!option) return code;
  return `${option.label} (${option.agency})`;
}

/**
 * Resolve preferred region for age rating display.
 * Prefer an explicit org/user region when provided.
 */
export function getUserRegion(preferredRegion?: string | null): string {
  if (preferredRegion && preferredRegion in RATING_AGENCY_BY_REGION) {
    return preferredRegion;
  }

  if (typeof localStorage !== 'undefined') {
    const savedRegion = localStorage.getItem('userRegion');
    if (savedRegion && savedRegion in RATING_AGENCY_BY_REGION) {
      return savedRegion;
    }
  }

  return 'US';
}

/**
 * Get the appropriate age rating for a region.
 * Tries preferred agencies for that region first, then falls back to the first available rating.
 */
export function getRegionAwareAgeRating(
  ageRatings?: AgeRating[],
  preferredRegion?: string | null,
): AgeRating | null {
  if (!ageRatings || ageRatings.length === 0) {
    return null;
  }

  if (ageRatings.length === 1) {
    return ageRatings[0];
  }

  const region = getUserRegion(preferredRegion);
  const preferredAgencies = RATING_AGENCY_BY_REGION[region] || RATING_AGENCY_BY_REGION.DEFAULT;

  for (const agency of preferredAgencies) {
    const rating = ageRatings.find((entry) => entry.agency === agency);
    if (rating) return rating;
  }

  return ageRatings[0];
}

/**
 * Format age rating for display.
 * Example: "ESRB Teen" or "PEGI 12"
 */
export function formatAgeRating(ageRating: AgeRating | null): string | null {
  if (!ageRating) return null;
  return `${ageRating.agency} ${ageRating.ratingValue}`;
}

/**
 * Get descriptors from age rating.
 */
export function getAgeRatingDescriptors(ageRating: AgeRating | null): string[] {
  return ageRating?.descriptors || [];
}

/**
 * Set user's region preference (client-side fallback).
 */
export function setUserRegion(region: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem('userRegion', region);
}
